import { ShieldStar, Bank, Lightning, Target, Warning, Lightbulb } from "@phosphor-icons/react";

function Card({ title, icon: Icon, accent = "amber", children }) {
  return (
    <div className={`rounded-sm border border-zinc-800 bg-zinc-900/40 p-5 hover:border-${accent}-500/30 transition-colors`}>
      <div className="flex items-center gap-2 mb-3">
        <Icon size={16} weight="bold" className={`text-${accent}-400`} />
        <h3 className="font-heading text-sm font-bold text-zinc-100 tracking-tight">{title}</h3>
      </div>
      <div className="text-[12px] leading-relaxed text-zinc-400 space-y-2">{children}</div>
    </div>
  );
}

export default function EmergencyFund() {
  return (
    <div className="px-5 py-6 space-y-6 max-w-[1200px]" data-testid="emergency-fund-page">
      {/* Hero */}
      <div className="rounded-sm border border-emerald-500/30 bg-gradient-to-br from-emerald-500/10 via-zinc-900/40 to-zinc-950 p-6">
        <div className="flex items-center gap-3 mb-3">
          <ShieldStar size={28} weight="fill" className="text-emerald-400" />
          <h2 className="font-heading text-2xl font-bold text-zinc-50 tracking-tight">Emergency Fund</h2>
        </div>
        <p className="text-[13px] text-zinc-400 leading-relaxed max-w-2xl">
          Cash that exists for one purpose: <span className="text-emerald-300">to keep one bad month from becoming a bad decade.</span>
          Job loss, medical bill, busted transmission, dead furnace — pick your flavor. Without a fund, every emergency
          becomes a credit-card balance compounding at 24%.
        </p>
      </div>

      <Card title="How much — 3 to 6 months of expenses" icon={Target} accent="emerald">
        <p>The number is <strong className="text-zinc-200">monthly expenses</strong>, not income. Add up rent, utilities, food, transport, insurance, minimum debt payments — your bare-bones survival number.</p>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 my-3">
          <div className="p-3 rounded-sm bg-zinc-950/60 border border-zinc-800">
            <p className="text-[10px] uppercase tracking-wider text-amber-400 font-bold mb-1">3 months</p>
            <p className="text-[11px] text-zinc-300">Stable W-2 job, low-debt, two-income household, no dependents.</p>
          </div>
          <div className="p-3 rounded-sm bg-zinc-950/60 border border-zinc-800">
            <p className="text-[10px] uppercase tracking-wider text-amber-400 font-bold mb-1">6 months</p>
            <p className="text-[11px] text-zinc-300">Single income, dependents, mortgage, specialized career, or older home/car.</p>
          </div>
          <div className="p-3 rounded-sm bg-zinc-950/60 border border-zinc-800">
            <p className="text-[10px] uppercase tracking-wider text-amber-400 font-bold mb-1">9–12 months</p>
            <p className="text-[11px] text-zinc-300">Self-employed, commission-only, freelancer, or nearing retirement.</p>
          </div>
        </div>
        <p className="text-[11px] text-zinc-500 italic">If your bare-bones expenses are $3,500/month, your 6-month target is $21,000. Don't budget around your <em>nice-to-have</em> life — budget around your <em>survival</em> life.</p>
      </Card>

      <Card title="Where to keep it — high-yield savings, period" icon={Bank} accent="blue">
        <p>The fund needs three traits, in this order: <strong className="text-zinc-200">safe, liquid, productive</strong>.</p>
        <ul className="list-disc pl-5 space-y-1.5 my-2">
          <li><strong className="text-zinc-200">High-Yield Savings Account (HYSA)</strong> — FDIC-insured, 4–5% APY in 2025/26 rate environments, withdraw in 1–3 days. The default answer.</li>
          <li><strong className="text-zinc-200">Money market funds</strong> — slightly higher yield, same-day liquidity, technically not FDIC-insured but extremely safe (Treasury-backed).</li>
          <li><strong className="text-zinc-200">Short-term T-bills</strong> — 4-week or 13-week, state-tax-free in the US, ladder them so something matures every 4 weeks.</li>
        </ul>
        <p className="text-red-300/80 text-[11px] mt-2">
          <Warning size={11} weight="fill" className="inline mr-1 -mt-0.5 text-red-400" />
          <strong>Not</strong> the stock market, <strong>not</strong> crypto, <strong>not</strong> CDs that lock up funds, <strong>not</strong> your checking account where you'll spend it.
        </p>
      </Card>

      <Card title="How to build it fast" icon={Lightning} accent="amber">
        <ol className="list-decimal pl-5 space-y-2 my-2">
          <li><strong className="text-zinc-200">Stage 1 — $1,000 starter buffer.</strong> Sell stuff. Skip eating out for 30 days. Pause investing temporarily. Cancel subscriptions. Whatever it takes to get to $1k in 4–6 weeks.</li>
          <li><strong className="text-zinc-200">Stage 2 — 1 month of expenses.</strong> Automate $200–500 per paycheck. Direct deposit any windfall (tax refund, bonus, gift) straight into the HYSA — pretend it never existed.</li>
          <li><strong className="text-zinc-200">Stage 3 — full 3–6 months.</strong> Maintain the auto-transfers but resume investing. The fund is now your floor — protect it.</li>
        </ol>
        <p className="text-[11px] text-zinc-500 italic mt-2">A $250/month auto-transfer compounds to $3,000 in a year. Boring is the goal. The fund's job isn't to grow — it's to stand watch.</p>
      </Card>

      <Card title="Pro tips" icon={Lightbulb} accent="violet">
        <ul className="list-disc pl-5 space-y-1.5">
          <li><strong className="text-zinc-200">Separate it from your checking account.</strong> Different bank, no debit card. Friction is your friend.</li>
          <li><strong className="text-zinc-200">Define what counts as an emergency in advance.</strong> Wedding gift = no. ER visit = yes. Black Friday TV = no. Roof leak = yes.</li>
          <li><strong className="text-zinc-200">Replenish first</strong> after using it — before any new investing.</li>
          <li><strong className="text-zinc-200">Don't over-build.</strong> Past 6–12 months for most people is dead money. The opportunity cost grows the bigger the fund gets.</li>
        </ul>
      </Card>
    </div>
  );
}
